// Copyright (c) Ping. All rights reserved.
//
// Pre-auth host for account recovery (V3 mnemonic flow). Two-stage:
//
//   Stage 1 — restore identity from backup
//     1. POST /v1/auth/start { method, identifier }  → challenge_id
//     2. user submits OTP + 12-word recovery phrase, host fires
//        POST /v1/auth/recover/verify { challenge_id, code } → recovery_token
//     3. GET /v1/recovery/blobs (Bearer recovery_token) → ciphertext
//     4. restoreFromRecoveryBlobV3(ciphertext, normalizedMnemonic):
//        - SDK Argon2id decryptBackup → SDK identity export S
//        - install SDK identity (identityManager.wrapIdentity)
//        - stash the DeviceGroup snapshot for post-bootstrap import
//        - HKDF(S) → AES-GCM decrypt the auth companion →
//          restoreUserIdentityFromBackup so /v1/auth/verify signs with
//          the SAME pubkey the BE pinned at signup
//
//   Stage 2 — finalize sign-in
//     5. POST /v1/auth/start  → fresh challenge_id (the first one was
//        consumed by recover/verify)
//     6. user enters the new OTP → verifySmsCode / verifyEmailCode →
//        access/refresh tokens stored locally → AuthenticatedUser
//
// P2.4 legacy fast-fail: if the fetched blob is not a V3 envelope (old
// v2 JSON / legacy bytes), `restoreFromRecoveryBlobV3` throws
// `LegacyRecoveryBlobError` BEFORE we fire the second /v1/auth/start —
// so we never burn a second OTP only to 403 on verify.

import { useCallback, useRef, useState } from 'react';

import { MessagingClient } from 'ping-openmls-sdk';

import { getRecoveryBlob, RecoveryMnemonicRestoreScreen } from '@ping/ui';
import { detectContactKind } from '@ping/ui/src/auth/contactDetect';

import { log } from '@core/log/log';
import {
  verifyEmailCode,
  verifySmsCode,
} from '@core/network';
import { api } from '@core/network/api';
import { setAccessToken } from '@core/network/interceptor';
import { storeIdentity } from '@core/persistence/identity';
import { storeWrappedRefreshToken } from '@core/persistence/tokens';

import type { AuthenticatedUser } from './flow/AuthFlowCoordinator';
import {
  LegacyRecoveryBlobError,
  restoreFromRecoveryBlobV3,
} from './recoveryBackupV3';

export interface RecoveryRestoreHostProps {
  onCancel: () => void;
  /**
   * Fires once the second OTP verifies and we have access/refresh
   * tokens stamped into local storage. The `user` shape matches
   * what `AuthFlowCoordinator` hands to App.tsx so the bootstrap
   * path is identical for both sign-in and recovery flows.
   */
  onRestored: (user: AuthenticatedUser) => void;
}

interface AuthStartWireBody {
  method: 'email' | 'phone';
  identifier: string;
}

interface AuthStartWireResponse {
  challenge_id: string;
}

interface RecoverVerifyResponse {
  account_id: string;
  recovery_token: string;
  expires_in: number;
}

export function RecoveryRestoreHost(props: RecoveryRestoreHostProps): JSX.Element {
  const { onCancel, onRestored } = props;
  const [challengeId, setChallengeId] = useState<string | null>(null);
  // Cached so stage-2's second /v1/auth/start can reuse the same
  // identifier without a re-prompt. Set inside `handleSendCode`.
  const identifierRef = useRef<{
    value: string;
    kind: 'email' | 'phone';
  } | null>(null);
  // Stage 2's challenge id. Distinct from the stage-1 one (which is
  // consumed by recover/verify).
  const finalizeChallengeIdRef = useRef<string | null>(null);

  const handleSendCode = useCallback(async (identifier: string) => {
    const kind = detectContactKind(identifier);
    if (kind === 'unknown') throw new Error('Enter a valid email or phone number.');
    const body: AuthStartWireBody = {
      method: kind === 'email' ? 'email' : 'phone',
      identifier,
    };
    const resp = await api.post<AuthStartWireResponse>('/v1/auth/start', body);
    setChallengeId(resp.challenge_id);
    identifierRef.current = { value: identifier, kind: body.method };
    log.info({ event: 'auth.recovery.start.ok', method: body.method });
  }, []);

  const handleRestore = useCallback(
    async (input: { code: string; mnemonic: string }) => {
      if (challengeId === null) {
        throw new Error('Request a code first.');
      }

      // Validate + canonicalise the user-entered phrase BEFORE spending
      // the OTP — a typo'd mnemonic shouldn't burn the recovery
      // challenge.
      let normalizedMnemonic: string;
      try {
        normalizedMnemonic = await MessagingClient.normalizeMnemonicPhrase(
          input.mnemonic,
        );
      } catch {
        throw new Error(
          "That doesn't look like a valid 12-word recovery phrase. Check the words and try again.",
        );
      }

      const verifyResp = await api.post<RecoverVerifyResponse>('/v1/auth/recover/verify', {
        challenge_id: challengeId,
        code: input.code,
      });
      log.info({ event: 'auth.recovery.verify.ok' });

      const blob = await getRecoveryBlob({ recoveryJwt: verifyResp.recovery_token });

      // Restore from the V3 envelope. This parses + validates the
      // envelope FIRST (throwing LegacyRecoveryBlobError on anything
      // that isn't v3) BEFORE we fire the second /v1/auth/start below —
      // so a legacy blob fails fast without burning a second OTP.
      try {
        await restoreFromRecoveryBlobV3(blob.ciphertext, normalizedMnemonic);
      } catch (err) {
        if (err instanceof LegacyRecoveryBlobError) {
          log.warn({ event: 'auth.recovery.legacyBlob' });
          // P2.4: clear user message; do NOT proceed to stage 2.
          throw new Error(err.message);
        }
        throw err;
      }
      log.info({ event: 'auth.recovery.restored', mode: 'blob.v3' });

      // Stage 2: fire a fresh /v1/auth/start so the user has a new OTP
      // to feed into /v1/auth/verify (the first OTP was burned by
      // /v1/auth/recover/verify). The screen advances to 'finalize'
      // automatically when this resolves.
      const ident = identifierRef.current;
      if (ident === null) {
        throw new Error('Identifier missing — restart the recovery flow.');
      }
      const startResp = await api.post<AuthStartWireResponse>('/v1/auth/start', {
        method: ident.kind,
        identifier: ident.value,
      } satisfies AuthStartWireBody);
      finalizeChallengeIdRef.current = startResp.challenge_id;
      log.info({ event: 'auth.recovery.finalize.start_sent' });
    },
    [challengeId],
  );

  const handleFinalize = useCallback(
    async (input: { code: string }) => {
      const ident = identifierRef.current;
      const finalChallenge = finalizeChallengeIdRef.current;
      if (ident === null || finalChallenge === null) {
        throw new Error('Recovery state was lost — please restart.');
      }
      // Re-use the existing /v1/auth/verify path so token storage,
      // device-binding-sig signing, and the AuthenticatedUser shape are
      // identical to the normal sign-in flow. The auth-layer user
      // identity key restored above is what `getUserIdentityPubkeyBytes`
      // (called inside verifyXCode) now signs with — the BE recognises
      // the account.
      const verifyArgs = {
        challengeId: finalChallenge,
        code: input.code,
        // Legacy fields below kept for the SmsVerifyBody/EmailVerifyBody
        // contract; the wire verify uses crypto helpers internally.
        deviceId: '',
        signaturePublicKey: '',
      };
      const result =
        ident.kind === 'email'
          ? await verifyEmailCode(verifyArgs)
          : await verifySmsCode(verifyArgs);
      log.info({ event: 'auth.recovery.finalize.verify_ok' });
      // Persist the minted tokens the SAME way the OTP sign-in
      // coordinator does. Without this, a recovered session has NO
      // in-memory access token (bootstrap 401s) and NO persisted refresh
      // token, so the next reload bounces to welcome.
      setAccessToken(result.accessToken, result.expiresIn);
      await Promise.all([
        storeWrappedRefreshToken(result.refreshToken),
        storeIdentity({
          accountId: result.accountId,
          deviceId: result.deviceId,
          displayName: result.displayName ?? '',
        }),
      ]);
      const user: AuthenticatedUser = {
        tokens: { hasAccessToken: true },
        identity: {
          accountId: result.accountId,
          deviceId: result.deviceId,
          displayName: result.displayName ?? '',
        },
      };
      onRestored(user);
    },
    [onRestored],
  );

  return (
    <RecoveryMnemonicRestoreScreen
      onSendCode={handleSendCode}
      onRestore={handleRestore}
      onFinalize={handleFinalize}
      onBack={onCancel}
    />
  );
}
